import { RoadmapProject } from '@/services/roadmapMarketplace';

/**
 * Shared utility function to extract projects from hierarchical roadmap/template structure
 * Used by RoadmapDetailPage and template edit pages to ensure consistent extraction logic
 */
export const extractProjectsFromHierarchy = (
  roadmapData: any,
  metadata?: {
    industry?: string;
    functional_area?: string;
    complexity_level?: number;
    tags?: string[];
  }
): RoadmapProject[] => {
  const extractedProjects: RoadmapProject[] = [];
  let projectIndex = 0;
  
  const hierarchicalContent = roadmapData.hierarchical_content || roadmapData.content;
  const hierarchy = hierarchicalContent?.hierarchy;
  
  // Get metadata from roadmapData or use provided metadata
  const finalMetadata = metadata || roadmapData.template_metadata || {
    industry: roadmapData.industry || '',
    functional_area: roadmapData.functional_area || '',
    complexity_level: roadmapData.complexity_level || 3,
    tags: roadmapData.tags || []
  };
  
  if (hierarchy?.visions) {
    hierarchy.visions.forEach((vision: any, _visionIndex: number) => {
      vision.objectives?.forEach((objective: any, _objectiveIndex: number) => {
        objective.strategies?.forEach((strategy: any, _strategyIndex: number) => {
          // Store strategy information for grouping
          const strategyId = `${roadmapData.id}-strategy-${strategy.title}`;
          
          // Process all projects/programs under strategy
          // A project is a PROGRAM if it has nested projects (project.projects array with items)
          // A project is a regular PROJECT if it has no nested projects
          if (strategy.projects && strategy.projects.length > 0) {
            const addProjectRecursively = (project: any, parentId: string, _projectIndexInParent: number) => {
              // Check if this is a PROGRAM (has nested projects)
              const isProgram = project.projects && Array.isArray(project.projects) && project.projects.length > 0;
              // CRITICAL: Use real database ID if available, otherwise generate temporary ID
              // This ensures timeline updates can find the correct project in the database
              const projectId = project.id || project.existing_id || (isProgram 
                ? `${roadmapData.id}-program-${projectIndex++}`
                : `${roadmapData.id}-project-${projectIndex++}`);
              
              extractedProjects.push({
                id: projectId,
                roadmap_template_id: roadmapData.id,
                name: project.title || project.name || 'Untitled Project',
                description: project.description || '',
                objectives: objective.title ? [objective.title] : [],
                expected_outcomes: [],
                complexity_level: project.complexity_level || finalMetadata.complexity_level || 3,
                estimated_duration_months: project.estimated_duration_months || project.duration_months || 6,
                start_month: project.start_month || 0,
                cost_drivers: {},
                cost_calculation_formula: '',
                status: project.status || 'PLANNING',
                hierarchy_type: isProgram ? 'PROGRAM' : 'PROJECT',
                parent_id: parentId,
                dependencies: project.dependencies || [],
                industry: finalMetadata.industry || '',
                functional_area: finalMetadata.functional_area || '',
                transformation_type: roadmapData.transformation_type || '',
                estimated_cost_range_min: project.estimated_cost || project.budget,
                estimated_cost_range_max: project.estimated_cost || project.budget,
                tags: project.tags || finalMetadata.tags || [],
                created_at: roadmapData.created_at || new Date().toISOString(),
                updated_at: roadmapData.updated_at || new Date().toISOString(),
                // Store strategy metadata (as any to allow custom properties)
                strategy_title: strategy.title,
                strategy_description: strategy.description,
                // Store program metadata if this is a program
                program_title: isProgram ? project.title || project.name : undefined,
                program_description: isProgram ? project.description : undefined,
              } as any);
              
              // Recursively add nested projects (children of this program/project)
              if (project.projects && Array.isArray(project.projects) && project.projects.length > 0) {
                project.projects.forEach((subProject: any, subIndex: number) => {
                  addProjectRecursively(subProject, projectId, subIndex);
                });
              }
              
              // Also handle sub_projects for backward compatibility
              if (project.sub_projects && Array.isArray(project.sub_projects) && project.sub_projects.length > 0) {
                project.sub_projects.forEach((subProject: any, subIndex: number) => {
                  addProjectRecursively(subProject, projectId, subIndex);
                });
              }
            };
            
            // Process all top-level projects/programs under strategy
            strategy.projects.forEach((project: any, projectIndexInStrategy: number) => {
              addProjectRecursively(project, strategyId, projectIndexInStrategy);
            });
          }
        });
      });
    });
  }
  
  return extractedProjects;
};

